import { z } from 'zod';

/*
 * Helpdesk: questions and requests to the Nirvik team (the default) or one of
 * the user's organizations, tracked as conversations with a status. See the
 * API's services/helpdesk.ts.
 */

export const HELP_TYPES = ['question', 'request', 'problem', 'account'] as const;
export type HelpType = (typeof HELP_TYPES)[number];
export const HELP_TYPE_LABELS: Record<HelpType, string> = {
  question: 'Question',
  request: 'Request (access, a course, a change)',
  problem: 'Something isn’t working',
  account: 'My account',
};

export const HELP_STATUSES = ['open', 'in_progress', 'waiting', 'resolved', 'closed'] as const;
export type HelpStatus = (typeof HELP_STATUSES)[number];
export const HELP_STATUS_LABELS: Record<HelpStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  waiting: 'Waiting on you',
  resolved: 'Resolved',
  closed: 'Closed',
};
/** The same statuses as admins see them. */
export const HELP_STATUS_ADMIN_LABELS: Record<HelpStatus, string> = {
  ...HELP_STATUS_LABELS,
  waiting: 'Waiting on requester',
};

export const HELP_SUBJECT_MAX = 120;
export const HELP_BODY_MAX = 5000;
export const HELP_MAX_FILES = 3;
export const HELP_FILE_MAX_BYTES = 5 * 1024 * 1024;
export const HELP_FILE_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  pdf: 'application/pdf',
};
/** Open requests a person may have per inbox, and messages a day. */
export const HELP_OPEN_PER_INBOX = 5;
export const HELP_MESSAGES_PER_DAY = 20;
/** Resolved requests close by themselves after this many days. */
export const HELP_AUTO_CLOSE_DAYS = 14;

const fileIds = z.array(z.string().uuid()).max(HELP_MAX_FILES).default([]);
const pageUrl = z
  .string()
  .max(500)
  .regex(/^\/[^\s]*$/)
  .nullable()
  .optional();

export const helpRequestInputSchema = z.object({
  orgId: z.string().uuid().nullable().optional(),
  type: z.enum(HELP_TYPES),
  subject: z.string().trim().min(3).max(HELP_SUBJECT_MAX),
  body: z.string().trim().min(1).max(HELP_BODY_MAX),
  pageUrl,
  fileIds,
});
export type HelpRequestInput = z.infer<typeof helpRequestInputSchema>;

export const helpReplySchema = z.object({
  body: z.string().trim().min(1).max(HELP_BODY_MAX),
  /** Admins only: a note other admins see, never the requester. */
  internal: z.boolean().default(false),
  fileIds,
});

export const helpUpdateSchema = z.object({
  status: z.enum(HELP_STATUSES).optional(),
  /** Admins only; null unassigns. */
  assigneeId: z.string().uuid().nullable().optional(),
});

export const helpQuerySchema = z.object({
  /** open (anything not resolved/closed), mine (assigned to me), unassigned, done (resolved/closed), all */
  view: z.enum(['open', 'mine', 'unassigned', 'done', 'all']).default('open'),
  type: z.enum(HELP_TYPES).optional(),
  q: z.string().trim().max(100).optional(),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type HelpQuery = z.infer<typeof helpQuerySchema>;

export const helpFileRequestSchema = z.object({
  filename: z.string().trim().min(1).max(200),
  contentType: z.string().max(100),
  sizeBytes: z.number().int().min(1).max(HELP_FILE_MAX_BYTES),
});

/** The stored type for an attachment's name, or null if it isn't allowed. */
export const helpFileType = (filename: string) =>
  HELP_FILE_TYPES[filename.toLowerCase().split('.').pop() ?? ''] ?? null;
